import * as PrimitiveRadixDialog from '@radix-ui/react-dialog';
import styled from 'styled-components';
import { tokens } from '@cellula/react-theme-patient';
import { Flex, IconButton, SafeArea, Spacer, VisuallyHidden, Button, Stack, Text } from '@cellula/react';
import { AnalyticProvider, useAnalyticContext } from '@components/analytic_link/analytic_link.provider';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import { MenuListItem } from './_header_navigation_menu_list_item.component';
import { useSelectedEntrance } from '@src/application/use_selected_entrance';
import { LicenceHolderService } from '@services/licence_holder.service';
import { useService } from '@visiba/instantiation';
import { HeaderNavController } from '@controllers/header_nav.controller';
import { usePresentation } from '@visiba/presentation';
import { useUser } from '@services/auth/use_user';
import { motion, Variants } from 'framer-motion';
import { RouterService } from '@services/router.service';
import { AuthService } from '@services/auth/auth.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { SyntheticEvent, Fragment } from 'react';
import { useCountedIssues } from '@services/unhandled_issues.service/use_counted_issues';
import { useFeatureFlag } from '@services/feature_flag/use_feature_flag';
import { IdentityApiService } from '@api/identity_api.service';

interface TEMP_PROPS {
	__TEMP__onSignIn?: () => void;
	__TEMP__onSignOut?: () => void;
}

const StyledDialogContent = styled(PrimitiveRadixDialog.Content)`
	inset: 0;
	position: fixed;
	z-index: calc(var(--Z_INDEX_HACK, 0));
	display: flex;
	flex: 1;

	&:before {
		content: '';
		top: 0;
		left: 0;
		width: 200%;
		height: 200%;
		position: absolute;
		background: ${tokens.color.bg.page};
		z-index: -1;
	}
`;

const StyledContent = styled(motion.div)`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: 100%;
	height: 100%;
	background: ${tokens.color.bg.surface};
	display: flex;
	flex-wrap: wrap;
	z-index: calc(var(--Z_INDEX_HACK, 0));
`;

const StyledHeader = styled.header`
	min-height: ${tokens.size.x6};
	display: flex;
	justify-content: flex-end;
	align-items: center;
	width: 100%;
	color: ${tokens.color.content.interactive};
`;

const sidebarVariants: Variants = {
	open: () => ({
		transition: {
			type: 'spring',
			stiffness: 40,
			restDelta: 2,
		},
		x: 0,
	}),
	closed: {
		transition: {
			delay: 0.1,
			type: 'spring',
			stiffness: 400,
			damping: 40,
		},
		x: '100%',
	},
};

interface Props extends TEMP_PROPS {}

export function NavigationMenu(props: Props): JSX.Element {
	const AS_TEMP_PROPS = props;

	const [entrance] = useSelectedEntrance();
	const licenceHolderService = useService(LicenceHolderService);
	const headerNavController = useService(HeaderNavController);
	const { showLinkHome, showLinkReceptionPicker, showLinkMyCases } = usePresentation(headerNavController.presentation);
	const countedIssues = useCountedIssues(['ongoing', 'completed']);
	const analyticService = useService(AnalyticService);
	const user = useUser();

	function analyticQuirk_handleOpenMenuClick(): void {
		analyticService.trackEvent(AnalyticCategories.TopNavigation, 'Clicked: menu opened');
	}

	function analyticQuirk_handleCloseMenuClick(): void {
		analyticService.trackEvent(AnalyticCategories.TopNavigation, 'Clicked: menu closed');
	}

	return (
		<AnalyticProvider category={AnalyticCategories.TopNavigation}>
			<PrimitiveRadixDialog.Root>
				<PrimitiveRadixDialog.Trigger asChild>
					<IconButton
						variant='neutral'
						icon='menu'
						tooltipAlign='end'
						size='small'
						onClick={analyticQuirk_handleOpenMenuClick}
						text={withTranslation('PW.Component.Header.NavigationMenu.TriggerLabel')}
					/>
				</PrimitiveRadixDialog.Trigger>

				<PrimitiveRadixDialog.Portal>
					<StyledDialogContent asChild>
						<StyledContent
							variants={sidebarVariants}
							initial={{ x: '100%', opacity: 0 }}
							animate={{ x: 0, opacity: 1 }}
							exit={{
								x: 'calc(100% + 150px)',
							}}
							transition={{
								type: 'spring',
								stiffness: 350,
								damping: 30,
							}}
						>
							<SafeArea>
								<StyledHeader>
									<PrimitiveRadixDialog.Close asChild>
										<IconButton
											text={withTranslation('PW.Component.Header.NavigationMenu.Close')}
											size='medium'
											icon='x'
											variant='neutral'
											tooltipAlign='end'
											onClick={analyticQuirk_handleCloseMenuClick}
										/>
									</PrimitiveRadixDialog.Close>
								</StyledHeader>

								<VisuallyHidden>
									<PrimitiveRadixDialog.Title>
										{withTranslation('PW.Component.Header.NavigationMenu.Title')}
									</PrimitiveRadixDialog.Title>
									<PrimitiveRadixDialog.Description>
										{withTranslation('PW.Component.Header.NavigationMenu.Description')}
									</PrimitiveRadixDialog.Description>
								</VisuallyHidden>

								<Flex as='ul' gap={tokens.spacing.x1} fluid='horizontal'>
									{showLinkHome ? (
										<MenuListItem analyticQuirk__text='Home-tab' to={licenceHolderService.alias} icon='house'>
											{withTranslation('PW.Component.Header.NavigationMenu.Home')}
										</MenuListItem>
									) : null}

									{showLinkReceptionPicker ? (
										<MenuListItem
											analyticQuirk__text={'Reception-tab'}
											to={entrance?.route ? entrance.route : `${licenceHolderService.alias}/change-unit`}
											icon='medicalPlus'
										>
											{entrance?.name || withTranslation('PW.Component.Header.NavigationMenu.Reception')}
										</MenuListItem>
									) : null}

									{showLinkMyCases ? (
										<MenuListItem
											analyticQuirk__text={user == null ? 'My cases-tab  (not signed-in)' : 'My cases-tab '}
											to={`${licenceHolderService.alias}/visits`}
											icon='archive'
											authenticate
											badgeCount={countedIssues}
										>
											{withTranslation('PW.Component.Header.NavigationMenu.Visits')}
										</MenuListItem>
									) : null}

									<MenuListItem
										analyticQuirk__text={`${'My account'}${user == null} ? ' (not signed-in) : ''`}
										to={`${licenceHolderService.alias}/my-account`}
										icon='user'
										authenticate
									>
										{withTranslation('PW.Component.Header.NavigationMenu.MyAccount')}
									</MenuListItem>
								</Flex>

								<AuthButtonSection {...AS_TEMP_PROPS} />

								<Spacer vertical={tokens.spacing.x2} />
							</SafeArea>
						</StyledContent>
					</StyledDialogContent>
				</PrimitiveRadixDialog.Portal>
			</PrimitiveRadixDialog.Root>
		</AnalyticProvider>
	);
}

interface AuthButtonSectionProps extends TEMP_PROPS {}

function AuthButtonSection({ __TEMP__onSignIn, __TEMP__onSignOut }: AuthButtonSectionProps): JSX.Element {
	const licenceHolderService = useService(LicenceHolderService);
	const authService = useService(AuthService);
	const routerService = useService(RouterService);
	const analyticService = useService(AnalyticService);
	const category = useAnalyticContext();

	const user = useUser();

	function handleSignInClick(_: SyntheticEvent<HTMLButtonElement>): void {
		if (user == null) {
			analyticService.trackEvent(category, 'Clicked: Login-button');

			if (__TEMP__onSignIn) {
				__TEMP__onSignIn();

				return;
			}
		}
	}

	function handleSignOutClick(): void {
		authService.terminateActiveIdentity().then(() => {
			const url = '/' + licenceHolderService.alias;
			if (!window.__TEMP__) {
				routerService.navigateByUrl(url);
			} else {
				__TEMP__onSignOut?.();
			}

			analyticService.trackEvent(category, 'Clicked: Logout-button');
		});
	}

	return (
		<Flex grow={1} justify='flex-end' fluid='horizontal'>
			<Stack gap={tokens.spacing.x2} fluid='horizontal'>
				{user == null ? (
					<PrimitiveRadixDialog.Close asChild>
						<Button
							size='large'
							iconPosition='trailing'
							variant='primary'
							icon='login'
							color={tokens.color.bg.interactive}
							onClick={handleSignInClick}
						>
							{withTranslation('Common.Button.SignIn')}
						</Button>
					</PrimitiveRadixDialog.Close>
				) : (
					<Fragment>
						<Text.p style={{ textAlign: 'center' }} variant='labelRegular' color={tokens.color.content.muted}>
							{withTranslation('PW.Component.Header.NavigationMenu.SignedInInfo', { name: user.commonName })}
						</Text.p>
						<AuthButton onClick={handleSignOutClick} />
					</Fragment>
				)}
			</Stack>
		</Flex>
	);
}

function AuthButton(props: { onClick: (event: SyntheticEvent<HTMLButtonElement>) => void }) {
	const licenceHolderService = useService(LicenceHolderService);
	const identityApiService = useService(IdentityApiService);
	const flag = useFeatureFlag('CareExperience.PW.Idp');

	if (flag) {
		return (
			<form
				action={identityApiService.getSignOutPath(`/${licenceHolderService.alias}`)}
				method='post'
				style={{
					display: 'contents',
				}}
			>
				<Button
					type='submit'
					size='large'
					variant='secondary'
					icon='logout'
					iconPosition='trailing'
					color={tokens.color.bg.interactive}
				>
					{withTranslation('Common.Button.SignOut')}
				</Button>
			</form>
		);
	}

	return (
		<PrimitiveRadixDialog.Close asChild>
			<Button
				size='large'
				variant='secondary'
				icon='logout'
				iconPosition='trailing'
				onClick={props.onClick}
				color={tokens.color.bg.interactive}
			>
				{withTranslation('Common.Button.SignOut')}
			</Button>
		</PrimitiveRadixDialog.Close>
	);
}
